import React, {memo} from "react";
import {formikPropertyChanged} from "./index";
import {useTranslation} from "react-i18next";
import FormSection from "../../../components/FormSection";
import Grid from "@mui/material/Grid";
import EditLatLngMap from "../../../components/maps/EditLatLngMap";
import MDInput from "../../../materialDashboard/components/MDInput";
import InputAdornment from "@mui/material/InputAdornment";
import MDTypography from "../../../materialDashboard/components/MDTypography";
import {renderTimeViewClock} from "@mui/x-date-pickers/timeViewRenderers";
import {DatePicker, TimePicker} from "@mui/x-date-pickers";
import IncidentSelect from "./IncidentSelect";
import IncidentField from "./IncidentField";
import {
    formatISODate,
    parseISODate,
    parseISODateTime,
    formatISODateTime
} from "../../../utils/date";
import {FieldWithTooltip} from "./FormTooltip";
import RegionAutocomplete from "../../../components/inputs/RegionAutocomplete";
import ProvinceAutocomplete from "../../../components/inputs/ProvinceAutocomplete";
import MunicipalityAutocomplete from "../../../components/inputs/MunicipalityAutocomplete";
import WorkgroupAutocomplete from "../../../components/inputs/WorkgroupsAutocomplete/WorkgroupAutocomplete";
import {preventValueChangeOnScroll} from "../../../utils/input";

const attributes = [
    'date', 'timeRange', 'datetime', 'workgroupId', 'regionId', 'provinceId', 'municipalityId', 'locationName', 'elevationRelease',
    'aspect', 'lng', 'lat', 'geom',
]

const Localization = memo(({formik, incidentRegistry, inputDisabled, incident}) => {
    const {t} = useTranslation(['incidents', 'enums']);

    const timeRangeRegistry = incidentRegistry.data?.timeRange ?? [];
    const aspectRegistry = incidentRegistry.data?.aspect ?? [];

    const handlePositionChange = (position) => {
        formik.setFieldValue('geom', {
            type: 'Point',
            coordinates: [position.lng, position.lat]
        })
    }

    const handleRegionChange = async (regionId) => {
        // await is needed to prevent this issue https://github.com/jaredpalmer/formik/issues/2266
        await formik.setFieldValue('regionId', regionId ?? null);
        await formik.setFieldValue('provinceId', null);
        formik.setFieldValue('municipalityId', null);
    }

    const handleProvinceChange = async (provinceId) => {
        // await is needed to prevent this issue https://github.com/jaredpalmer/formik/issues/2266
        await formik.setFieldValue('municipalityId', null);
        formik.setFieldValue('provinceId', provinceId ?? null);
    }

    return <FormSection title={t('incidentForm.localizationTitle')}
                        error={attributes.some(attr => Boolean(formik.errors[attr]) && formik.touched[attr])}>
        <Grid container rowSpacing={3} columnSpacing={4}>
            <Grid item xs={12} md={6}>
                <Grid container columnSpacing={1} rowSpacing={3}>
                    <Grid item xs={12}>
                        <FieldWithTooltip message={t('incidentForm.tooltip.date')}>
                            <DatePicker value={parseISODate(formik.values.date)}
                                        label={t('incidentForm.date')}
                                        onChange={(date) => {
                                            formik.setFieldValue("date", formatISODate(date));
                                            const datetime = parseISODateTime(formik.values.datetime);
                                            datetime?.setFullYear(date.getFullYear());
                                            datetime?.setMonth(date.getMonth());
                                            datetime?.setDate(date.getDate());
                                            formik.setFieldValue("datetime", datetime);
                                        }}
                                        sx={{width: "100%"}}

                                        slots={{
                                            // override default slots for better styling
                                            textField: MDInput
                                        }}
                                        slotProps={{
                                            textField: {
                                                variant: 'standard',
                                                error: formik.touched.date && Boolean(formik.errors.date),
                                                helperText: (formik.touched.date && formik.errors.date)
                                            }
                                        }}
                                        disableFuture
                                        disabled={inputDisabled}
                            />
                        </FieldWithTooltip>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <IncidentSelect name='timeRange'
                                        formik={formik}
                                        registry={timeRangeRegistry}
                                        disabled={inputDisabled}/>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <TimePicker value={parseISODateTime(formik.values.datetime)}
                                    label={t('incidentForm.datetime')}
                                    onChange={(time) => formik.setFieldValue("datetime", formatISODateTime(time))}
                                    sx={{width: "100%"}}
                                    slots={{
                                        // override default slots for better styling
                                        textField: MDInput
                                    }}
                                    viewRenderers={{
                                        hours: renderTimeViewClock,
                                        minutes: renderTimeViewClock,
                                    }}
                                    slotProps={{
                                        textField: {
                                            variant: 'standard',
                                            error: formik.touched.datetime && Boolean(formik.errors.datetime),
                                            helperText: (formik.touched.datetime && formik.errors.datetime)
                                        }
                                    }}
                                    ampm={false}
                                    disabled={inputDisabled}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <WorkgroupAutocomplete name="workgroupId"
                                               value={formik.values.workgroupId}
                                               disabled={inputDisabled}
                                               onChange={(workgroupId) => formik.setFieldValue('workgroupId', workgroupId)}
                                               initialOption={incident?.workgroup}
                                               workgroupFilters={{
                                                   acl__incidents_write: true,
                                               }}
                                               MDInputProps={{
                                                   name: 'workgroupId',
                                                   error: formik.touched.workgroupId && Boolean(formik.errors.workgroupId),
                                                   helperText: formik.touched.workgroupId && formik.errors.workgroupId
                                               }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <RegionAutocomplete name="regionId"
                                            value={formik.values.regionId}
                                            disabled={inputDisabled}
                                            onChange={handleRegionChange}
                                            MDInputProps={{
                                                name: 'regionId',
                                                label: t('incidentForm.regionId'),
                                                error: formik.touched.regionId && Boolean(formik.errors.regionId),
                                                helperText: formik.touched.regionId && formik.errors.regionId
                                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <ProvinceAutocomplete name="provinceId"
                                              regionId={formik.values.regionId}
                                              value={formik.values.provinceId}
                                              disabled={inputDisabled}
                                              onChange={handleProvinceChange}
                                              MDInputProps={{
                                                  name: 'provinceId',
                                                  label: t('incidentForm.provinceId'),
                                                  error: formik.touched.provinceId && Boolean(formik.errors.provinceId),
                                                  helperText: formik.touched.provinceId && formik.errors.provinceId
                                              }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FieldWithTooltip message={t('incidentForm.tooltip.municipalityId')}>
                            <MunicipalityAutocomplete name="municipalityId"
                                                      regionId={formik.values.regionId}
                                                      provinceId={formik.values.provinceId}
                                                      value={formik.values.municipalityId}
                                                      disabled={inputDisabled}
                                                      onChange={(municipalityId) => formik.setFieldValue('municipalityId', municipalityId)}
                                                      MDInputProps={{
                                                          name: 'municipalityId',
                                                          label: t('incidentForm.municipalityId'),
                                                          error: formik.touched.municipalityId && Boolean(formik.errors.municipalityId),
                                                          helperText: formik.touched.municipalityId && formik.errors.municipalityId
                                                      }}
                            />
                        </FieldWithTooltip>
                    </Grid>

                    <Grid item xs={12}>
                        <FieldWithTooltip message={t('incidentForm.tooltip.locationName')}>
                            <MDInput name="locationName"
                                     label={t('incidentForm.locationName')}
                                     value={formik.values.locationName ?? ""}
                                     onChange={formik.handleChange}
                                     error={formik.touched.locationName && Boolean(formik.errors.locationName)}
                                     helperText={formik.touched.locationName && formik.errors.locationName}
                                     variant="standard"
                                     fullWidth
                                     disabled={inputDisabled}
                            />
                        </FieldWithTooltip>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FieldWithTooltip message={t('incidentForm.tooltip.elevationRelease')}>
                            <IncidentField formik={formik}
                                           name="elevationRelease"
                                           type="number"
                                           unitOfMeasure="m"
                                           disabled={inputDisabled}/>
                        </FieldWithTooltip>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        <FieldWithTooltip message={t('incidentForm.tooltip.aspect')}>
                            <IncidentSelect name='aspect' formik={formik} registry={aspectRegistry}
                                            disabled={inputDisabled}/>
                        </FieldWithTooltip>
                    </Grid>

                    <Grid item xs={12}>
                        <Grid container columnSpacing={1}>
                            <Grid item xs={12} md={6}>
                                <MDInput name="geom.coordinates[1]"
                                         label={t('incidentForm.lat')}
                                         value={formik.values.geom?.coordinates?.[1] ?? ""}
                                         onChange={formik.handleChange}
                                         error={formik.touched?.geom?.coordinates?.[1] && Boolean(formik.errors?.geom?.coordinates?.[1])}
                                         helperText={formik.touched?.geom?.coordinates?.[1] && formik.errors?.geom?.coordinates?.[1]}
                                         variant="standard"
                                         fullWidth
                                         InputProps={{
                                             endAdornment: <InputAdornment position="end">°</InputAdornment>,
                                         }}
                                         type="number"
                                         onWheel={preventValueChangeOnScroll}
                                         disabled={inputDisabled}
                                />
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <MDInput name="geom.coordinates[0]"
                                         label={t('incidentForm.lng')}
                                         value={formik.values.geom?.coordinates?.[0] ?? ""}
                                         onChange={formik.handleChange}
                                         error={formik.touched?.geom?.coordinates?.[0] && Boolean(formik.errors?.geom?.coordinates?.[0] )}
                                         helperText={formik.touched?.geom?.coordinates?.[0]  && formik.errors?.geom?.coordinates?.[0] }
                                         variant="standard"
                                         fullWidth
                                         InputProps={{
                                             endAdornment: <InputAdornment position="end">°</InputAdornment>,
                                         }}
                                         type="number"
                                         onWheel={preventValueChangeOnScroll}
                                         disabled={inputDisabled}
                                />
                            </Grid>
                        </Grid>
                        <MDTypography variant="caption" fontWeight="regular" color="text" component="div" sx={{mt: 1}}>
                            (EPSG:4326 WGS 84) {t('incidentForm.epsgioLink')} <a
                            href="https://epsg.io/transform#s_srs=23032&t_srs=4326" target="_blank"
                            rel="noreferrer">epsg.io</a>
                        </MDTypography>
                    </Grid>
                </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
                <EditLatLngMap lat={formik.values.geom?.coordinates?.[1]}
                               lng={formik.values.geom?.coordinates?.[0]}
                               onChange={handlePositionChange}
                               style={{height: "400px"}}
                               disabled={inputDisabled}
                />
            </Grid>
        </Grid>
    </FormSection>

}, (prevProps, nextProps) => formikPropertyChanged(prevProps, nextProps, attributes))


export default Localization;